// ─────────────────────────────────────────────────────────────────────────────
// UNA SOLA AUTORIDAD DE PRECIO DE APLICACIÓN
//
// Espejo de `precioDePaquete.js`, que ya resolvió esto para paquetes. No se inventa un patrón
// nuevo: se extiende uno probado, con su mismo reparto de responsabilidades.
//
//   · `precioDeLaFila`      el precio con la fila del catálogo en la mano
//   · `precioParaPintar`    qué puede pintar una pantalla · nunca 0, nunca NaN, nunca undefined
//   · `aportacionAlTotal`   qué suma · un 0 MARCADO como omitido, no un 0 que finge ser precio
//   · `esOfertable`         si se puede OFRECER, que no es si se puede COBRAR
//
// ── POR QUÉ EXISTE ──────────────────────────────────────────────────────────
//
// Había tres lectores de dinero de aplicación con lógicas ligeramente distintas, y los tres
// convertían un precio nulo en 0:
//
//   appCatalog.js         `numeric(… ?? extra_price, 0)`   ← el camino del cobro de extras
//   quoteCatalog.js:819   `numeric(… ?? app.price, 0)`     ← el precio del bloque
//   quoteCatalogAdmin.js  `catalogNumber(…, 0)`            ← la semilla del admin
//
// Un cero no es la ausencia de un precio. Para una aplicación que REQUIERE COTIZACIÓN, ese
// cero la vuelve gratis; y «gratis» es una afirmación comercial que nadie tomó.
//
// ── TRES INVARIANTES ────────────────────────────────────────────────────────
//
//   1. FALLA CERRADO. Si no resuelve devuelve `null`, nunca 0 y nunca un número viejo.
//   2. OFERTA Y COBRO SON DISTINTOS. `commercial_mode` dice si se ofrece; `sellable` dice si
//      se puede cobrar automáticamente. Una SCOPED_QUOTE se ofrece y no se cobra sola.
//   3. MODO DESCONOCIDO = RECHAZO. Nunca cae en FIXED, ni en vendible, ni en gratis.
// ─────────────────────────────────────────────────────────────────────────────

export const MODO = Object.freeze({
  FIJO: 'FIXED',
  ADICIONAL: 'ADDON_ONLY',
  CON_ALCANCE: 'SCOPED_QUOTE',
});

export const AUTORIDAD = Object.freeze({
  /** El precio salió de la tabla canónica con su modo declarado. */
  CANONICA: 'quote_catalog_applications',
  /** Fila viva sin `commercial_mode`: las 28 de antes de la V2. Resuelve por compatibilidad. */
  LEGADO: 'LEGACY_PRICED_NO_MODE',
  /** Resuelto, y su importe automático está PROHIBIDO. No es lo mismo que no resolver. */
  REQUIERE_COTIZACION: 'SCOPED_QUOTE',
  /** No se pudo resolver: hay que negarse a pintar un número y a sumar. */
  SIN_RESOLVER: 'UNRESOLVED',
  /** Modo comercial que no existe. Se rechaza en vez de adivinar. */
  MODO_DESCONOCIDO: 'UNKNOWN_MODE_FAIL_CLOSED',
  /** Legado deshabilitado: ni precio ni camino transaccional. */
  PENDIENTE: 'PENDING_PRICE_NOT_TRANSACTABLE',
});

const texto = (v) => (v === null || v === undefined ? '' : String(v)).trim();
const MERCADO_NACIONAL = 'nacional';

const numeroONull = (crudo) => {
  if (crudo === null || crudo === undefined || crudo === '') return null;
  const n = Number(crudo);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/**
 * EL PRECIO CON LA FILA DEL CATÁLOGO EN LA MANO.
 *
 * @param fila      fila de `quote_catalog_applications`, o null
 * @param opciones  { applicationId, mercado } · `mercado` 'nacional' | 'internacional'
 *
 * El mercado por defecto es el internacional, por la misma razón que en paquetes: es el que
 * la autoridad declara siempre. Para aplicaciones el internacional puede faltar en las filas
 * legadas, y entonces NO se sustituye por el nacional — se devuelve sin resolver.
 */
export function precioDeLaFila(fila, opciones = {}) {
  const applicationId = texto(opciones.applicationId ?? fila?.id);
  const mercado = texto(opciones.mercado).toLowerCase() === MERCADO_NACIONAL
    ? 'nacional' : 'internacional';

  const no = (autoridad, extra = {}) => ({
    precio: null,
    autoridad,
    applicationId,
    mercado,
    resuelto: false,
    requiereCotizacion: false,
    transaccionable: false,
    ...extra,
  });

  if (!applicationId || !fila) return no(AUTORIDAD.SIN_RESOLVER);

  const modo = texto(fila.commercial_mode ?? fila.commercialMode).toUpperCase();
  const estado = texto(fila.pricing_status ?? fila.pricingStatus).toUpperCase();

  // ── REQUIERE COTIZACIÓN ───────────────────────────────────────────────────
  //
  // RESUELTO y sin importe. Es la distinción que el esquema viejo no sabía hacer: no es «no sé
  // cuánto vale», es «su importe no se fija solo».
  if (modo === MODO.CON_ALCANCE || estado === 'SCOPED_QUOTE') {
    return {
      precio: null,
      autoridad: AUTORIDAD.REQUIERE_COTIZACION,
      applicationId,
      mercado,
      resuelto: true,
      requiereCotizacion: true,
      transaccionable: false,
      ofertable: true,
    };
  }

  // ── LEGADO DESHABILITADO ──────────────────────────────────────────────────
  if (estado === 'PENDING_PRICE') return no(AUTORIDAD.PENDIENTE);

  // ── MODO DESCONOCIDO · FALLA CERRADO ──────────────────────────────────────
  //
  // Un modo que no está en la lista no se interpreta. La fila legada SIN modo es otra cosa y
  // se trata abajo: ausencia no es invención.
  if (modo && modo !== MODO.FIJO && modo !== MODO.ADICIONAL) {
    return no(AUTORIDAD.MODO_DESCONOCIDO);
  }

  const crudo = mercado === 'nacional'
    ? (fila.base_price ?? fila.basePrice ?? fila.precio)
    : (fila.international_net ?? fila.internationalNet);

  const n = numeroONull(crudo);
  if (n === null) return no(AUTORIDAD.SIN_RESOLVER);

  // Un FIXED o ADDON_ONLY declarado exige importe POSITIVO. El 0 sólo es legal en las filas
  // legadas sin modo —las cuatro CORE incluidas por cupo—.
  if (modo && n <= 0) return no(AUTORIDAD.SIN_RESOLVER);

  return {
    precio: n,
    autoridad: modo ? AUTORIDAD.CANONICA : AUTORIDAD.LEGADO,
    applicationId,
    mercado,
    resuelto: true,
    requiereCotizacion: false,
    /** `sellable` sólo puede RESTAR: una fila sin precio no se vuelve cobrable. */
    transaccionable: fila.sellable !== false,
    ofertable: true,
    modo: modo || null,
    soloAdicional: modo === MODO.ADICIONAL,
  };
}

/**
 * EL PRECIO BUSCANDO LA FILA EN UN CATÁLOGO YA CARGADO.
 */
export function precioCanonicoDeAplicacion(catalogo, { applicationId, mercado } = {}) {
  const lista = Array.isArray(catalogo) ? catalogo
    : (Array.isArray(catalogo?.applications) ? catalogo.applications : []);
  const id = texto(applicationId);
  const fila = id ? lista.find((a) => texto(a?.id) === id) || null : null;
  return precioDeLaFila(fila, { applicationId: id, mercado });
}

/**
 * LO QUE UNA PANTALLA PUEDE PINTAR.
 *
 * Nunca 0, nunca NaN, nunca `undefined`. El texto de cliente es el MISMO que ya usa
 * `precioDePaquete.js`: no se inventa copia nueva para decir lo mismo.
 */
export function precioParaPintar(p, { ambito = 'staff' } = {}) {
  if (p && p.requiereCotizacion) {
    return ambito === 'cliente'
      ? { texto: 'Valor confirmado por el estudio', precio: null, bloquea: true, requiereCotizacion: true }
      : { texto: 'Requiere cotización', precio: null, bloquea: true, requiereCotizacion: true };
  }
  if (!p || !p.resuelto || p.precio === null) {
    return ambito === 'cliente'
      ? { texto: 'Valor confirmado por el estudio', precio: null, bloquea: true, requiereCotizacion: false }
      : { texto: 'Sin precio canónico', precio: null, bloquea: true, requiereCotizacion: false };
  }
  return {
    texto: '$' + p.precio.toLocaleString('en-US') + ' USD',
    precio: p.precio,
    bloquea: false,
    requiereCotizacion: false,
  };
}

/**
 * LO QUE SUMA A UN TOTAL.
 *
 * El 0 que devuelve cuando no resuelve está MARCADO como omitido y NO es un precio: significa
 * «esta línea no aporta un importe automático». Nadie debe persistirlo como precio comercial.
 */
export function aportacionAlTotal(p) {
  if (p && p.requiereCotizacion) {
    return { suma: 0, omitido: true, motivo: AUTORIDAD.REQUIERE_COTIZACION };
  }
  if (!p || !p.resuelto || p.precio === null) {
    return { suma: 0, omitido: true, motivo: p?.autoridad || AUTORIDAD.SIN_RESOLVER };
  }
  if (!p.transaccionable) {
    return { suma: 0, omitido: true, motivo: 'NOT_TRANSACTABLE' };
  }
  return { suma: p.precio, omitido: false, motivo: null };
}

/**
 * SE PUEDE OFRECER · que no es «se puede cobrar».
 *
 * Retira `price > 0` como autoridad de ofertabilidad. Una SCOPED_QUOTE se ofrece y se
 * recomienda sin importe automático; lo que no puede es entrar en el cobro.
 */
export function esOfertable(p) {
  if (!p) return false;
  // Una SCOPED_QUOTE se ofrece SIN importe: eso es lo que `price > 0` no sabia hacer.
  if (p.requiereCotizacion) return true;
  // Pero un adicional a precio CERO tampoco se ofrece: regalarlo es una decision
  // comercial que nadie tomo. El 0 legado SI resuelve como precio -0 no es «no se»-, y
  // por eso esta decision vive aqui y no en `precioDeLaFila`: son dos preguntas.
  return Boolean(p.resuelto && p.precio !== null && p.precio > 0);
}

/** Puede entrar en un camino de cobro automático. */
export function esTransaccionable(p) {
  return Boolean(p && p.resuelto && p.precio !== null && p.transaccionable && !p.requiereCotizacion);
}

/**
 * LA PUERTA DE IDENTIDAD PREVIA.
 *
 * Toda BRAND_APPLICATION presupone un sistema de identidad utilizable, y da igual quién lo
 * hizo: una marca ajena pero válida habilita la aplicación.
 *
 * El predicado es EXPLÍCITO a propósito. No se infiere de texto libre ni de que el cliente
 * tenga un proyecto: se exige una declaración.
 */
export function puedeVenderseConIdentidad(fila, identidad = {}) {
  const exige = (fila?.requires_existing_identity ?? fila?.requiresExistingIdentity) !== false;
  if (!exige) return { permitido: true, motivo: null };
  const utilizable = identidad?.identidadUtilizable === true;
  if (utilizable) {
    return { permitido: true, motivo: null, autoriaExterna: identidad?.autoria !== 'feria' };
  }
  return {
    permitido: false,
    motivo: 'SIN_IDENTIDAD_UTILIZABLE',
    recomendacion: 'BRANDING_PRODUCT_OR_PROJECT',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// EL POLICY DE ESCRITURA · C
//
// Había OCHO sitios que escribían dinero de aplicación con `|| 0`. Ocho sitios son ocho
// opiniones; esto es una. Lo que se decide aquí no es «qué número pintar» sino «qué número
// se puede PERSISTIR», que es una pregunta distinta y más estricta.
//
// Medido en producción el 2026-10-02: `project_applications` tiene 34 filas, TODAS con
// `is_extra = false` y `extra_price = 0`. Nunca se ha persistido un extra. La rama insegura
// de esos ocho sitios no es un defecto histórico: es un arma cargada que no ha disparado.
// Ninguna fila histórica necesita reescribirse.
//
// Y hay un noveno convertidor, en el esquema: `project_applications.extra_price` es nulable
// pero su DEFAULT es 0. OMITIR la columna escribe 0. Por eso esto devuelve `valor: null`
// explícito y quien escribe tiene que ponerlo, no saltárselo.
// ─────────────────────────────────────────────────────────────────────────────

export const ESCRITURA = Object.freeze({
  /** 0 LEGÍTIMO: el contrato ya la pagó. Es el único cero que se persiste. */
  CONTRACTUAL: 'INCLUDED_BY_CONTRACT',
  /** El importe lo manda el cobro confirmado, no el catálogo. */
  DEL_PAGO: 'PAID_COBRO',
  /** Importe canónico del catálogo. */
  COBRABLE: 'CHARGEABLE',
  /** Resuelta y sin importe automático. Se persiste la LÍNEA con precio NULL, nunca 0. */
  SIN_IMPORTE: 'NO_AUTOMATIC_AMOUNT',
  /** No resuelve. No se escribe nada: quien llama falla cerrado. */
  RECHAZADA: 'REFUSED_UNRESOLVED',
});

/**
 * QUÉ IMPORTE SE PUEDE PERSISTIR.
 *
 * @param esExtra        la línea se cobra aparte, o el contrato la incluye
 * @param precio         veredicto de `precioDeLaFila`
 * @param pagoConfirmado el cobro, si ya existe
 *
 * El vocabulario de `extra_status` NO se amplía: en producción sólo viven 'incluida' y
 * 'incluida_en_cotizacion', y el código ya usa 'pagada' y 'pendiente_pago'. Una aplicación
 * que requiere cotización sigue siendo 'pendiente_pago' —lo está— y lo que la distingue es
 * que su importe va NULL, no que se invente un estado nuevo en la base.
 */
export function precioDeExtraParaPersistir({ esExtra = false, precio = null, pagoConfirmado = null } = {}) {
  if (!esExtra) {
    return {
      valor: 0,
      estado: 'incluida',
      persistible: true,
      autoridad: ESCRITURA.CONTRACTUAL,
      revisionHumana: false,
      motivo: null,
    };
  }

  // Un extra YA PAGADO vale lo que se cobró. El catálogo puede haber cambiado de precio
  // desde entonces, y el histórico no se reescribe.
  const delPago = numeroONull(
    pagoConfirmado?.monto ?? pagoConfirmado?.amount ?? pagoConfirmado?.total ?? pagoConfirmado?.importe,
  );
  if (delPago !== null && delPago > 0) {
    return {
      valor: delPago,
      estado: 'pagada',
      persistible: true,
      autoridad: ESCRITURA.DEL_PAGO,
      revisionHumana: false,
      motivo: null,
    };
  }

  if (precio && precio.requiereCotizacion) {
    return {
      valor: null,
      estado: 'pendiente_pago',
      persistible: true,
      autoridad: ESCRITURA.SIN_IMPORTE,
      revisionHumana: true,
      motivo: AUTORIDAD.REQUIERE_COTIZACION,
    };
  }

  if (!precio || !precio.resuelto || precio.precio === null || !precio.transaccionable) {
    return {
      valor: null,
      estado: 'pendiente_pago',
      persistible: false,
      autoridad: ESCRITURA.RECHAZADA,
      revisionHumana: true,
      motivo: precio?.autoridad || AUTORIDAD.SIN_RESOLVER,
    };
  }

  return {
    valor: precio.precio,
    estado: 'pendiente_pago',
    persistible: true,
    autoridad: ESCRITURA.COBRABLE,
    revisionHumana: false,
    motivo: null,
  };
}

/**
 * LA FILA DE CATÁLOGO DE UN ENTREGABLE CONTRATADO.
 *
 * `complete-client-brief` inserta en `quote_catalog_applications` los entregables de una
 * cotización aceptada que aún no son filas, porque `project_applications` tiene clave ajena
 * contra el catálogo. Lo hacía con NUEVE columnas y la tabla tiene CUATRO sin default y
 * NOT NULL: `layer`, `pricing_status`, `sellable` y `service_family`.
 *
 * MEDIDO el 2026-10-02 contra la base viva: esa inserción falla HOY con
 * `23502 null value in column "layer"`, y con ella falla el cierre del brief entero. Es
 * alcanzable: de las 21 cotizaciones aceptadas, `web_squarespace` no tiene fila de catálogo.
 *
 * La fila nace ARCHIVADA y NO VENDIBLE: existe para que el proyecto resuelva, no para que
 * nadie la pueda comprar. `commercial_mode` va nulo a propósito —no es una decisión
 * comercial del máster— y eso la deja en la rama legada del invariante.
 */
export function filaDeCatalogoParaEntregableContratado(entregable = {}, opciones = {}) {
  const id = texto(entregable.application_id ?? entregable.id);
  if (!id) return null;
  const sello = opciones.timestamp || new Date().toISOString();
  const importe = numeroONull(entregable.extra_price ?? entregable.base_price ?? entregable.basePrice);
  return {
    id,
    name: texto(entregable.name) || id,
    category: texto(entregable.category) || 'Alcance de cotizacion',
    // Un entregable contratado SÍ tiene un importe: el que se contrató. Si no se sabe, es 0
    // por contrato —ya está pagado dentro del paquete—, no 0 por ignorancia.
    base_price: importe === null ? 0 : importe,
    layer: 'SPECIALIZED',
    pricing_status: 'PRICED',
    sellable: false,
    service_family: texto(opciones.serviceFamily) || 'branding',
    can_be_addon: false,
    show_in_packages: false,
    visible: false,
    status: 'archived',
    sort_order: 9000 + (Number(opciones.index) || 0),
    created_at: sello,
    updated_at: sello,
  };
}
